import React, { useState } from "react";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import { toast } from "sonner";
import api from "../lib/api";
import { BRAND, ACTIVE_LOCATIONS } from "../lib/constants";
import { Mail, MapPin } from "lucide-react";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "General inquiry", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Thanks — we'll be in touch shortly.");
      setForm({ name: "", email: "", subject: "General inquiry", message: "" });
    } catch (err) {
      const d = err?.response?.data?.detail;
      const msg = Array.isArray(d) ? d[0]?.msg : d;
      toast.error(msg || "Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const input = "w-full px-4 py-4 bg-transparent border border-black/15 text-ink placeholder:text-ink-muted focus:outline-none focus:border-brand focus:ring-1 focus:ring-brand transition-colors";

  return (
    <>
      <SEO
        title="Contact District Wellness Center | DC Medical Cannabis Dispensary"
        description="Contact District Wellness Center. Questions about products, delivery, or your medical cannabis registration — email us at info@districtwellnesscenter.com."
        path="/contact"
        breadcrumbs={[{ name: "Contact", path: "/contact" }]}
      />
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-6 py-24 sm:py-32 grid lg:grid-cols-2 gap-16">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-brand mb-5">Get in Touch</div>
            <h1 className="font-serif text-5xl sm:text-7xl leading-[0.95] tracking-tight">
              Say <em className="text-brand">hello</em>.
            </h1>
            <p className="mt-6 text-ink-muted text-lg leading-relaxed max-w-md">
              Questions about products, delivery, or your medical cannabis registration?
              We usually respond within a business day.
            </p>

            <div className="mt-10 space-y-6">
              <a href={`mailto:${BRAND.email}`} className="flex items-center gap-3 text-ink hover:text-brand" data-testid="contact-email">
                <Mail size={18} className="text-brand" /> {BRAND.email}
              </a>
              <a href={BRAND.phoneHref} className="flex items-center gap-3 text-ink hover:text-brand" data-testid="contact-phone">
                <span className="w-[18px] text-brand inline-flex justify-center">☎</span> {BRAND.phone}
              </a>
              <div className="border-l-2 border-brand pl-5">
                <div className="text-[10px] uppercase tracking-[0.3em] text-ink-muted mb-3 flex items-center gap-2"><MapPin size={12} /> Our Dispensary</div>
                <ul className="space-y-3">
                  {ACTIVE_LOCATIONS.map((loc) => (
                    <li key={loc.id}>
                      <div className="text-[11px] uppercase tracking-[0.25em] text-brand">{loc.label}</div>
                      <div className="font-serif text-lg">{loc.street}, {loc.city}</div>
                      {loc.phone && <a href={loc.phoneHref} className="text-sm text-ink-muted hover:text-brand">☎ {loc.phone}</a>}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          <form onSubmit={submit} className="p-8 sm:p-10 bg-[#FAFAFA] border border-black/10 space-y-5" data-testid="contact-form">
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] text-ink-muted block mb-2">Name</label>
              <input
                required value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className={input}
                placeholder="Your name"
                data-testid="contact-name"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] text-ink-muted block mb-2">Email</label>
              <input
                type="email" required value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className={input}
                placeholder="you@email.com"
                data-testid="contact-email-input"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] text-ink-muted block mb-2">Subject</label>
              <input
                value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                className={input}
                placeholder="How can we help?"
                data-testid="contact-subject"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] text-ink-muted block mb-2">Message</label>
              <textarea
                required rows={6} value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className={input}
                placeholder="Tell us what you need…"
                data-testid="contact-message"
              />
            </div>
            <button
              type="submit" disabled={loading}
              className="w-full bg-ink hover:bg-brand text-white px-6 py-4 text-[12px] uppercase tracking-[0.22em] transition-colors disabled:opacity-60"
              data-testid="contact-submit"
            >
              {loading ? "Sending…" : "Send Message"}
            </button>
          </form>
        </div>
      </section>

      {/* LONG-FORM: contact info + hours + geo coverage */}
      <section className="py-24 bg-[#FAFAFA] border-t border-black/10">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-[10px] uppercase tracking-[0.3em] text-brand mb-5">Ways to reach us</div>
          <h2 className="font-serif text-4xl sm:text-5xl leading-[1.05] tracking-tight">
            Talk to a licensed DC dispensary — in person, on the phone, or online.
          </h2>
          <div className="mt-8 space-y-5 text-ink-muted text-lg leading-relaxed">
            <p>
              District Wellness Center is a licensed medical cannabis dispensary at 4915 Georgia Ave NW,
              Washington, DC 20011. Walk-ins are welcome from Petworth, Columbia Heights, Brightwood, Shaw,
              Park View, and every neighborhood in upper Northwest DC — and same-day delivery is available
              across the entire District.
            </p>
            <p>
              Have a question about your DC medical cannabis registration, product effects, dosing, or a
              same-day delivery order that&rsquo;s already in flight? The fastest way to reach us is a phone
              call at <a href="tel:+12026217800" className="text-brand link-underline">(202) 621-7800</a>. For
              non-urgent questions, email <a href="mailto:info@districtwellnesscenter.com" className="text-brand link-underline">info@districtwellnesscenter.com</a> and
              we&rsquo;ll get back to you within a business day.
            </p>
          </div>

          <h3 className="font-serif text-2xl text-ink mt-12 mb-4">Store hours (open 7 days)</h3>
          <ul className="space-y-2 text-lg text-ink-muted">
            <li>Monday – Thursday · 8:00 AM – 10:00 PM</li>
            <li>Friday – Saturday · 8:00 AM – 11:00 PM</li>
            <li>Sunday · 9:00 AM – 9:00 PM</li>
          </ul>

          <h3 className="font-serif text-2xl text-ink mt-12 mb-4">DC neighborhoods we serve</h3>
          <p className="text-lg text-ink-muted leading-relaxed">
            Walk-ins from Petworth, Park View, Columbia Heights, Brightwood, 16th Street Heights, Shaw,
            Adams Morgan, Dupont Circle, Georgetown, Capitol Hill, Navy Yard, Foggy Bottom, Chinatown, Mount
            Pleasant, Woodley Park, Tenleytown, Brookland, Takoma DC, Fort Totten, Woodridge, Brentwood,
            Palisades, Chevy Chase DC and beyond. Delivery covers the same neighborhoods with same-day
            windows most weeknights.
          </p>

          <h3 className="font-serif text-2xl text-ink mt-12 mb-4">Licensed and legal</h3>
          <p className="text-lg text-ink-muted leading-relaxed">
            DWC operates under Growth and Wellness, LLC, licensed by the DC Alcoholic Beverage &amp;
            Cannabis Administration (ABCA-127954). All products are lab-tested and DC-compliant.
          </p>

          {/* Related links */}
          <div className="mt-16 pt-10 border-t border-black/10 grid sm:grid-cols-3 gap-6">
            <Link to="/menu" className="group block" data-testid="contact-related-menu">
              <div className="text-[10px] uppercase tracking-[0.3em] text-brand mb-2">Related</div>
              <div className="font-serif text-xl text-ink group-hover:text-brand">Shop the live menu →</div>
            </Link>
            <Link to="/delivery" className="group block" data-testid="contact-related-delivery">
              <div className="text-[10px] uppercase tracking-[0.3em] text-brand mb-2">Related</div>
              <div className="font-serif text-xl text-ink group-hover:text-brand">Same-day DC delivery →</div>
            </Link>
            <Link to="/faq" className="group block" data-testid="contact-related-faq">
              <div className="text-[10px] uppercase tracking-[0.3em] text-brand mb-2">Related</div>
              <div className="font-serif text-xl text-ink group-hover:text-brand">Read the FAQ →</div>
            </Link>
          </div>
        </div>
      </section>

      {/* Google review CTA */}
      <section className="bg-ink text-white py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-serif text-4xl sm:text-5xl leading-[1.05]">
            Already a patient? <em className="text-brand">Leave us a Google review.</em>
          </h2>
          <p className="mt-5 text-white/70 max-w-2xl mx-auto text-base sm:text-lg leading-relaxed">
            Your honest review on Google helps other patients find us and helps our local business grow.
          </p>
          <div className="mt-10">
            <a
              href="https://maps.app.goo.gl/ynJcSrirV4qpH1gz8"
              target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-2 bg-brand hover:bg-brand-600 text-white px-7 py-4 text-[11px] uppercase tracking-[0.22em]"
              data-testid="contact-google-review"
            >
              Leave a Google review →
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
